import { useTimezone } from '@/hooks/useTimezoneContext'
import { mergeDeliveryRouteLiveState } from '@/components/orders/delivery-route-stops'
import { deliveryShareQueries } from '@/lib/services/delivery-shares/delivery-shares.query'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'

function useDeliveryRouteTimezone(share) {
  const { pushTimezone, popTimezone } = useTimezone()
  const routeTimezone = share?.location?.address?.timezone || share?.location?.timezone

  useEffect(() => {
    if (!routeTimezone) return

    pushTimezone(routeTimezone)

    return () => popTimezone()
  }, [routeTimezone, pushTimezone, popTimezone])
}

export function useDeliveryRouteShare({ routeApi }) {
  const { isReadOnly } = routeApi.useLoaderData()
  const { deliveryShareId, orgId } = routeApi.useParams()
  const { data: share } = useSuspenseQuery({
    ...deliveryShareQueries.show({
      organizationId: orgId,
      deliveryShareId,
    }),
  })

  useDeliveryRouteTimezone(share)

  return {
    deliveryShareId,
    isReadOnly,
    orgId,
    share,
  }
}

export function useDeliveryRouteDetailShare({ routeApi }) {
  const { isReadOnly } = routeApi.useLoaderData()
  const { deliveryShareId, orgId } = routeApi.useParams()
  const shareQuery = useSuspenseQuery(
    deliveryShareQueries.show({
      organizationId: orgId,
      deliveryShareId,
    })
  )
  const liveStateQuery = useQuery({
    ...deliveryShareQueries.liveState({
      organizationId: orgId,
      deliveryShareId,
    }),
    refetchInterval: (query) => {
      const currentState = query.state.data

      return !currentState || (currentState.status === 'active' && !currentState.is_completed)
        ? 1000 * 15
        : false
    },
  })
  const refetchRouteDetail = useEffectEvent(() => {
    void shareQuery.refetch()
  })
  const share = mergeDeliveryRouteLiveState(shareQuery.data, liveStateQuery.data)

  useEffect(() => {
    const liveVersion = liveStateQuery.data?.route_version
    const detailVersion = shareQuery.data?.route_version

    if (liveVersion && liveVersion !== detailVersion) {
      refetchRouteDetail()
    }
  }, [
    liveStateQuery.data?.route_version,
    liveStateQuery.dataUpdatedAt,
    shareQuery.data?.route_version,
  ])

  useDeliveryRouteTimezone(share)

  return {
    deliveryShareId,
    isReadOnly,
    orgId,
    share,
  }
}
